import { object, string } from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";

import styled from "styled-components/macro";

import LinkListRow from "NavBar/components/LinkListRow";
import { menuTypographyProps } from "NavBar/components/NavLink";
import { navSectionKeys } from "NavBar/navBarStore";

import FnrTypography from "UI/FnrTypography";

import useHeight from "Utilities/Hooks/useHeight";
import dynHeight from "Utilities/interpolations/dynHeight";
import dynMarginBottom from "Utilities/interpolations/dynMarginBottom";
import dynMarginTop from "Utilities/interpolations/dynMarginTop";

import { useSignupsToDisplay } from "../signupsAndEventsStore";

import Count from "./Count";
import SignupIcon from "./SignupIcon";

const AnimatedSignupRow = ({
  timerRef,
  timerResetTrigger,
  signup,
  accentColor,
}) => {
  const signupsToDisplay = useSignupsToDisplay();

  const pendingInDom = useMemo(() => {
    return signupsToDisplay?.some((s) => s.ID === signup.ID);
  }, [signup.ID, signupsToDisplay]);

  const [show, setShow] = useState();
  const [inDom, setInDom] = useState();
  const timeoutRef = useRef();

  useEffect(() => {
    clearTimeout(timeoutRef.current);
    if (pendingInDom) {
      timerRef.current += 20;
      timeoutRef.current = setTimeout(() => {
        setInDom(pendingInDom);
      }, timerRef.current);
    } else {
      setShow(false);
      timeoutRef.current = setTimeout(() => setInDom(false), 200);
    }

    return () => clearTimeout(timeoutRef.current);
  }, [pendingInDom, timerRef, timerResetTrigger]);

  useEffect(() => {
    if (inDom) setShow(inDom);
  }, [inDom]);

  const contentRef = useRef();
  const contentHeight = useHeight(contentRef);

  if (!inDom) return null;
  return (
    <SLinkListRow
      $marginTop={show ? 5 : 0}
      $marginBottom={show ? 5 : 0}
      to={`/signups/${signup.ID}`}
      navSectionKey={navSectionKeys.signups}
      $variant={show ? "shown" : "hidden"}
      $height={show ? contentHeight : 0}
      contentRef={contentRef}
      $scale
    >
      <IconAndTitleWrapper>
        <SignupIcon signupListTypeID={signup.signupListTypeID} />
        <FnrTypography {...menuTypographyProps} noWrap={false}>
          {signup.title}
        </FnrTypography>
      </IconAndTitleWrapper>
      <Count
        count={signup.totalConfirmedSignups}
        max={signup.totalAvailableSlots}
        accentColor={accentColor}
      />
    </SLinkListRow>
  );
};

AnimatedSignupRow.propTypes = {
  timerRef: object,
  timerResetTrigger: object,
  signup: object,
  accentColor: string,
};

const SLinkListRow = styled(LinkListRow)`
  opacity: ${({ $variant }) => ($variant === "shown" ? 1 : 0)};
  transform: ${({ $variant, $scale }) =>
    $variant === "shown" ? "scale(1)" : $scale ? "scale(0.75)" : "scale(1)"};
  ${dynMarginTop}
  ${dynMarginBottom}
  ${dynHeight}
  transition: opacity 300ms, transform 300ms, height 300ms, margin 300ms;
  ${({ $variant }) => $variant === "hidden" && "* { pointer-events: none; }"}
`;

const IconAndTitleWrapper = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  margin-left: 4px;
`;

export default AnimatedSignupRow;
